import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Rss, RefreshCw, Wand2, Pencil, Trash2, Loader2, AlertTriangle, CheckCircle, Clock, RotateCcw } from 'lucide-react';

function decodeEntities(text: string): string {
  const el = document.createElement('textarea');
  el.innerHTML = text;
  return el.value;
}

const EDITORS = [
  { value: 'daniel_dobos', label: 'Daniel Dobos' },
  { value: 'adriana_dobos', label: 'Adriana Dobos' },
  { value: 'marcus_webb', label: 'Marcus Webb' },
  { value: 'elena_vasilescu', label: 'Elena Vasilescu' },
  { value: 'james_chen', label: 'James Chen' },
  { value: 'sofia_marinescu', label: 'Sofia Marinescu' },
  { value: 'daniel_novak', label: 'Daniel Novak' },
];

const RssScraper = () => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [newName, setNewName] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [scraping, setScraping] = useState<string | null>(null);
  const [editorSelection, setEditorSelection] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data: sources = [] } = useQuery({
    queryKey: ['rss_sources'],
    queryFn: async () => {
      const { data } = await supabase.from('rss_sources').select('*').order('created_at');
      return data || [];
    },
  });

  const { data: articles = [] } = useQuery({
    queryKey: ['scraped_articles'],
    queryFn: async () => {
      const { data } = await supabase.from('scraped_articles').select('*').neq('status', 'published').order('created_at', { ascending: false });
      return data || [];
    },
    refetchInterval: (query) => {
      const data = query.state.data as any[] | undefined;
      return data?.some(a => a.status === 'rewriting') ? 5000 : false;
    },
  });

  // Fetch latest jobs for articles that have a job linked
  const jobArticleIds = articles.filter(a => (a as any).last_rewrite_job_id).map(a => (a as any).last_rewrite_job_id);
  const { data: jobs = [] } = useQuery({
    queryKey: ['rewrite_jobs', jobArticleIds],
    queryFn: async () => {
      if (jobArticleIds.length === 0) return [];
      const { data } = await supabase.from('rewrite_jobs' as any).select('*').in('id', jobArticleIds);
      return (data || []) as any[];
    },
    enabled: jobArticleIds.length > 0,
    refetchInterval: (query) => {
      const data = query.state.data as any[] | undefined;
      return data?.some((j: any) => j.status === 'queued' || j.status === 'processing') ? 5000 : false;
    },
  });

  const getJobForArticle = (article: any) => {
    const jobId = article.last_rewrite_job_id;
    if (!jobId) return null;
    return jobs.find((j: any) => j.id === jobId) || null;
  };

  const addSource = useMutation({
    mutationFn: async () => {
      const name = newName.trim() || new URL(newUrl).hostname.replace('www.', '');
      const { error } = await supabase.from('rss_sources').insert({ name, feed_url: newUrl });
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['rss_sources'] }); setNewName(''); setNewUrl(''); toast.success('Source added'); },
  });

  const toggleSource = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      await supabase.from('rss_sources').update({ is_active: active }).eq('id', id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rss_sources'] }),
  });

  const deleteArticle = useMutation({
    mutationFn: async (id: string) => {
      await supabase.from('scraped_articles').delete().eq('id', id);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['scraped_articles'] }); toast.success('Article deleted'); },
  });

  const deleteSource = useMutation({
    mutationFn: async (id: string) => {
      await supabase.from('rss_sources').delete().eq('id', id);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['rss_sources'] }); toast.success('Source removed'); },
  });

  const bulkDeleteArticles = useMutation({
    mutationFn: async (ids: string[]) => {
      await supabase.from('scraped_articles').delete().in('id', ids);
    },
    onSuccess: () => {
      setSelected(new Set());
      qc.invalidateQueries({ queryKey: ['scraped_articles'] });
      toast.success('Selected articles deleted');
    },
  });

  const scrapeSource = async (source: any) => {
    setScraping(source.id);
    try {
      const { data, error } = await supabase.functions.invoke('scrape-rss', { body: { feed_url: source.feed_url } });
      if (error) throw error;
      for (const art of (data.articles || []).slice(0, 10)) {
        const exists = articles.find(a => a.original_url === art.url);
        if (!exists) {
          await supabase.from('scraped_articles').insert({
            original_title: art.title, original_url: art.url, original_content: art.content_snippet, source_id: source.id,
          });
        }
      }
      await supabase.from('rss_sources').update({ last_scraped_at: new Date().toISOString() }).eq('id', source.id);
      qc.invalidateQueries({ queryKey: ['scraped_articles'] });
      toast.success(`Scraped ${data.articles?.length || 0} articles`);
    } catch (e: any) { toast.error(e.message); } finally { setScraping(null); }
  };

  // Enqueue rewrite via the new endpoint
  const rewriteArticle = async (article: any) => {
    const editor = editorSelection[article.id] || 'marcus_webb';

    try {
      const { data, error } = await supabase.functions.invoke('enqueue-rewrite-article', {
        body: { article_id: article.id, editor },
      });

      if (error) throw error;

      if (data?.ok) {
        toast.info('Rewrite job queued — status will update automatically (30-60s).');
        qc.invalidateQueries({ queryKey: ['scraped_articles'] });
        qc.invalidateQueries({ queryKey: ['rewrite_jobs'] });
      } else {
        toast.error(`Failed to enqueue: ${data?.message || 'Unknown error'}`);
      }
    } catch (e: any) {
      toast.error(`Enqueue failed: ${e.message}`);
    }
  };

  const retryRewrite = async (article: any) => {
    // Reset article and create a new job
    await supabase.from('scraped_articles').update({ status: 'scraped', rewrite_error: null } as any).eq('id', article.id);
    qc.invalidateQueries({ queryKey: ['scraped_articles'] });
    // Then enqueue fresh
    rewriteArticle(article);
  };

  const editAndPublish = (article: any) => {
    navigate(`/admin/blog/new?from_rss=${article.id}`);
  };

  // Strict gate: only show Edit & Publish if status is rewritten AND content exists
  const canPublish = (article: any) => {
    return (article.status === 'rewritten' || article.status === 'needs_review') &&
      article.rewritten_en && article.rewritten_en.length > 100 &&
      article.rewritten_ro && article.rewritten_ro.length > 100;
  };

  const getStatusBadge = (article: any) => {
    const job = getJobForArticle(article);
    const status = article.status;
    const error = (article as any).rewrite_error;
    const aiScore = (article as any).ai_score;

    if (status === 'rewriting') {
      const jobStatus = job?.status;
      if (jobStatus === 'queued') return (
        <Badge variant="secondary" className="capitalize gap-1">
          <Clock className="w-3 h-3" /> Queued
        </Badge>
      );
      return (
        <Badge variant="secondary" className="capitalize gap-1">
          <Loader2 className="w-3 h-3 animate-spin" /> Processing
        </Badge>
      );
    }

    if (status === 'needs_review') {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1">
                <Badge variant="secondary" className="capitalize gap-1 bg-amber-500 text-white">
                  <AlertTriangle className="w-3 h-3" /> Needs Review
                </Badge>
                {aiScore !== null && aiScore !== undefined && (
                  <Badge variant="outline" className="text-xs">{aiScore}</Badge>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="text-xs">AI score {aiScore}/100 exceeded threshold. Content may need manual editing.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    if (status === 'rewritten' && canPublish(article)) {
      return (
        <div className="flex items-center gap-1">
          <Badge variant="default" className="capitalize gap-1 bg-green-600">
            <CheckCircle className="w-3 h-3" /> Rewritten
          </Badge>
          {aiScore !== null && aiScore !== undefined && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className={`text-xs ${aiScore <= 30 ? 'border-green-400 text-green-700' : aiScore <= 50 ? 'border-amber-400 text-amber-700' : 'border-red-400 text-red-700'}`}>
                    AI:{aiScore}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent><p className="text-xs">AI detection score: {aiScore}/100 (lower is better)</p></TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      );
    }

    if (status === 'scraped' && error) {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="destructive" className="capitalize gap-1 cursor-help">
                <AlertTriangle className="w-3 h-3" /> Failed
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="text-xs">{error}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    if (status === 'published') {
      return <Badge variant="default" className="capitalize">Published</Badge>;
    }

    return <Badge variant="secondary" className="capitalize">{status}</Badge>;
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold font-fraunces" style={{ color: 'var(--blue)' }}>RSS Scraper</h1>

      <Card>
        <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Rss className="w-4 h-4" /> RSS Sources</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input placeholder="Feed URL (e.g. https://example.com/feed/)" value={newUrl} onChange={e => setNewUrl(e.target.value)} className="flex-1" />
            <Input placeholder="Source name (optional)" value={newName} onChange={e => setNewName(e.target.value)} className="w-48" />
            <Button onClick={() => addSource.mutate()} disabled={!newUrl} className="gap-2"><Plus className="w-4 h-4" /> Add</Button>
          </div>
          <div className="space-y-2">
            {sources.map(s => (
              <div key={s.id} className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'var(--off-white)' }}>
                <Switch checked={s.is_active ?? true} onCheckedChange={v => toggleSource.mutate({ id: s.id, active: v })} />
                <div className="flex-1">
                  <p className="text-sm font-medium" style={{ color: 'var(--ink)' }}>{s.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{s.feed_url}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => scrapeSource(s)} disabled={scraping === s.id} className="gap-1 text-xs">
                  <RefreshCw className={`w-3 h-3 ${scraping === s.id ? 'animate-spin' : ''}`} /> Scrape
                </Button>
                <Button variant="ghost" size="icon" onClick={() => deleteSource.mutate(s.id)} className="text-destructive h-8 w-8"><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {selected.size > 0 && (
        <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-muted">
          <span className="text-sm font-medium">{selected.size} selected</span>
          <Button variant="destructive" size="sm" className="gap-1 text-xs" onClick={() => {
            if (window.confirm(`Delete ${selected.size} selected article(s)?`)) {
              bulkDeleteArticles.mutate([...selected]);
            }
          }}>
            <Trash2 className="w-3 h-3" /> Delete Selected
          </Button>
        </div>
      )}

      <div className="rounded-lg border border-border overflow-hidden bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={articles.length > 0 && selected.size === articles.length}
                  ref={(el) => {
                    if (el) {
                      (el as unknown as HTMLButtonElement).dataset.state =
                        selected.size > 0 && selected.size < articles.length ? 'indeterminate' : (el as unknown as HTMLButtonElement).dataset.state;
                    }
                  }}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      setSelected(new Set(articles.map(a => a.id)));
                    } else {
                      setSelected(new Set());
                    }
                  }}
                />
              </TableHead>
              <TableHead>Title</TableHead>
              <TableHead>Editor</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {articles.map(a => (
              <TableRow key={a.id} data-state={selected.has(a.id) ? 'selected' : undefined}>
                <TableCell>
                  <Checkbox
                    checked={selected.has(a.id)}
                    onCheckedChange={(checked) => {
                      setSelected(prev => {
                        const next = new Set(prev);
                        if (checked) next.add(a.id); else next.delete(a.id);
                        return next;
                      });
                    }}
                  />
                </TableCell>
                <TableCell className="text-sm max-w-md truncate" style={{ color: 'var(--ink)' }}>{decodeEntities(a.original_title)}</TableCell>
                <TableCell>
                  {(a.status === 'scraped' || a.status === 'needs_review') && (
                    <Select value={editorSelection[a.id] || 'marcus_webb'} onValueChange={v => setEditorSelection(prev => ({ ...prev, [a.id]: v }))}>
                      <SelectTrigger className="text-xs h-8 w-36"><SelectValue /></SelectTrigger>
                      <SelectContent>{EDITORS.map(e => <SelectItem key={e.value} value={e.value} className="text-xs">{e.label}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </TableCell>
                <TableCell>{getStatusBadge(a)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    {a.status === 'scraped' && !(a as any).rewrite_error && (
                      <Button variant="outline" size="sm" onClick={() => rewriteArticle(a)} className="gap-1 text-xs">
                        <Wand2 className="w-3 h-3" /> AI Rewrite
                      </Button>
                    )}
                    {a.status === 'scraped' && (a as any).rewrite_error && (
                      <Button variant="outline" size="sm" onClick={() => retryRewrite(a)} className="gap-1 text-xs">
                        <RotateCcw className="w-3 h-3" /> Retry
                      </Button>
                    )}
                    {a.status === 'needs_review' && (
                      <Button variant="outline" size="sm" onClick={() => retryRewrite(a)} className="gap-1 text-xs">
                        <RotateCcw className="w-3 h-3" /> Retry Rewrite
                      </Button>
                    )}
                    {a.status === 'rewriting' && (
                      <Badge variant="outline" className="text-xs gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" /> Working…
                      </Badge>
                    )}
                    {canPublish(a) && (
                      <Button variant="outline" size="sm" onClick={() => editAndPublish(a)} className="gap-1 text-xs">
                        <Pencil className="w-3 h-3" /> Edit & Publish
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => {
                      if (window.confirm('Delete this scraped article?')) deleteArticle.mutate(a.id);
                    }}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {articles.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-12">No scraped articles yet</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default RssScraper;
